import type { InvoiceExtractionIn } from '@/api/invoices';

/**
 * localStorage-backed drafts for the finding-detail invoice editor.
 *
 * The editor holds its in-progress facts in React state; if the page
 * unmounts mid-edit (a transient auth/query error, an accidental nav, a
 * tab close) that work is gone. Persisting the draft per document means a
 * remount can offer to restore it — the safety net for INP-502 even if some
 * other unmount path slips through the guard fixes.
 *
 * Privacy: drafts hold invoice facts (supplier, BN, amounts), so the key is
 * scoped by the signed-in user (a shared browser must not surface another
 * practitioner's draft) and every draft carries a timestamp — `load` drops
 * and removes anything older than the retention window so stale PII can't
 * linger indefinitely.
 *
 * All accessors swallow their own errors (private-mode / quota / disabled
 * storage) — a draft is a convenience, never load-bearing.
 */

const PREFIX = 'invoice-draft';
/** Drafts older than this are treated as expired and swept on read. */
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredDraft {
  savedAt: number;
  facts: InvoiceExtractionIn;
}

/** Per-(user, customer, document) key. Scoping by user keeps one
 *  practitioner's abandoned draft from surfacing to another on a shared
 *  browser; scoping by document is enough within a user (the editor is
 *  always for one attached document at a time). */
export function invoiceDraftKey(
  userKey: string | null,
  customerSlug: string | null,
  documentId: string,
): string {
  return `${PREFIX}:${userKey ?? '_'}:${customerSlug ?? '_'}:${documentId}`;
}

export function loadInvoiceDraft(key: string, now: number = Date.now()): InvoiceExtractionIn | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') return null;
    const stored = parsed as Partial<StoredDraft>;
    if (typeof stored.savedAt !== 'number' || stored.facts == null) return null;
    if (now - stored.savedAt > DRAFT_TTL_MS) {
      clearInvoiceDraft(key);
      return null;
    }
    return stored.facts;
  } catch {
    return null;
  }
}

export function saveInvoiceDraft(
  key: string,
  draft: InvoiceExtractionIn,
  now: number = Date.now(),
): void {
  try {
    const stored: StoredDraft = { savedAt: now, facts: draft };
    window.localStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // ignore — storage unavailable / over quota
  }
}

export function clearInvoiceDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** True when a saved draft differs from the currently-persisted facts, i.e.
 *  it holds edits worth restoring. A draft equal to the source is stale
 *  noise and should be ignored/cleared. */
export function draftHasChanges(
  draft: InvoiceExtractionIn | null,
  current: InvoiceExtractionIn,
): boolean {
  if (draft === null) return false;
  return JSON.stringify(draft) !== JSON.stringify(current);
}
